import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, ShieldOff } from "lucide-react";
import { toast } from "sonner";

export default function TwoFactorCard() {
  const { user, refresh } = useAuth();
  const [enabled, setEnabled] = useState(false);
  const [setup, setSetup] = useState(null); // { secret, qr_png_base64 }
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setEnabled(!!user?.totp_enabled);
  }, [user]);

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetup(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Setup failed"); }
    setBusy(false);
  };

  const enable = async () => {
    if (code.length !== 6) { toast.error("Enter the 6-digit code from your authenticator"); return; }
    setBusy(true);
    try {
      await api.post("/auth/2fa/enable", { code });
      toast.success("2FA enabled");
      setSetup(null); setCode("");
      setEnabled(true);
      if (refresh) refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not enable 2FA"); }
    setBusy(false);
  };

  const disable = async () => {
    if (code.length !== 6) { toast.error("Enter your current 6-digit code to disable"); return; }
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { code });
      toast.success("2FA disabled");
      setCode("");
      setEnabled(false);
      if (refresh) refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not disable 2FA"); }
    setBusy(false);
  };

  return (
    <Card data-testid="two-factor-card" className="rounded-none p-6 border border-bone">
      <div className="flex items-start gap-3 mb-4">
        {enabled ? <ShieldCheck className="h-6 w-6 text-emerald-600" /> : <ShieldOff className="h-6 w-6 text-slate-400" />}
        <div className="flex-1">
          <div className="micro-label text-gold">Two-Factor Authentication</div>
          <h3 className="font-display text-xl mt-1">{enabled ? "2FA is ON" : "2FA is OFF"}</h3>
          <p className="text-sm text-slate-500 mt-1 leading-relaxed">
            {enabled ? "Your account requires a 6-digit code from your authenticator app on every sign-in." : "Add an extra layer of protection. You'll scan a QR code with Google/Microsoft Authenticator (or any TOTP app)."}
          </p>
        </div>
      </div>

      {!enabled && !setup && (
        <Button data-testid="start-2fa-button" onClick={startSetup} disabled={busy} className="rounded-none bg-ink text-ivory hover:bg-gold hover:text-ink uppercase tracking-[0.2em] text-xs font-semibold">
          {busy ? "Starting…" : "Enable 2FA"}
        </Button>
      )}

      {!enabled && setup && (
        <div className="space-y-3">
          <div className="text-xs text-slate-600 leading-relaxed">
            1. Scan this QR code with your authenticator app.<br />
            2. Enter the 6-digit code below to confirm.
          </div>
          <img alt="2FA QR" data-testid="2fa-qr" src={`data:image/png;base64,${setup.qr_png_base64}`} className="w-44 h-44 border border-bone" />
          <details className="text-xs text-slate-500"><summary className="cursor-pointer">Can't scan? Show setup key</summary><div className="mt-2 font-mono-num bg-ivory px-2 py-1 border border-bone select-all">{setup.secret}</div></details>
          <div className="space-y-1.5">
            <Label className="text-xs">Verification code</Label>
            <Input data-testid="2fa-verify-code" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="123 456" className="font-mono-num tracking-widest" />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setSetup(null); setCode(""); }}>Cancel</Button>
            <Button data-testid="confirm-2fa-button" onClick={enable} disabled={busy} className="rounded-none bg-ink text-ivory hover:bg-gold hover:text-ink uppercase tracking-[0.2em] text-xs font-semibold">{busy ? "Verifying…" : "Confirm & enable"}</Button>
          </div>
        </div>
      )}

      {enabled && (
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Enter your current 2FA code to disable</Label>
            <Input data-testid="2fa-disable-code" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="123 456" className="font-mono-num tracking-widest max-w-xs" />
          </div>
          <Button data-testid="disable-2fa-button" onClick={disable} disabled={busy} variant="outline" className="rounded-none border-rose-300 text-rose-700 hover:bg-rose-50 uppercase tracking-[0.2em] text-xs font-semibold">{busy ? "Disabling…" : "Disable 2FA"}</Button>
        </div>
      )}
    </Card>
  );
}
